import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Instagram, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Logo from "./Logo";
import { ORDER_URL as DEFAULT_ORDER_URL } from "../data/site";
import { useSiteContent } from "../data/SiteContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_HOURS = [
  { days: "Monday", time: "Closed" },
  { days: "Tue — Thu", time: "12:00 PM – 8:00 PM" },
  { days: "Fri — Sat", time: "12:00 PM – 10:00 PM" },
  { days: "Sunday", time: "12:00 PM – 8:00 PM" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { content } = useSiteContent();
  const f = content?.footer || {};
  const ORDER_URL = content?.links?.order_url || DEFAULT_ORDER_URL;

  const addr1 = f.address_line1 || "110-46 Merrick Blvd";
  const addr2 = f.address_line2 || "Jamaica, New York 11433";
  const phone = f.phone || "(718) 297-4BTW (4289)";
  const tel = f.tel || "7182974289";
  const emailAddr = f.email || "info@btwnyc.com";
  const ig = f.instagram || "https://instagram.com/burgerstacoswings";
  const fb = f.facebook || "https://facebook.com/burgerstacoswings";
  const tt = f.tiktok || "https://www.tiktok.com/@btwnyc";
  const igHandle = f.ig_handle || "@burgerstacoswings";
  const ttHandle = f.tiktok_handle || "@btwnyc";
  const hours = (f.hours && f.hours.length ? f.hours : DEFAULT_HOURS);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed! Watch for BTW deals & drops.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="footer"
      data-testid="site-footer"
      className="relative bg-[#0B0B0B] text-white pt-16 pb-8 overflow-hidden"
    >
      {/* Watermark stamp */}
      <svg
        className="btw-stamp absolute -right-24 top-1/2 -translate-y-1/2 hidden md:block"
        width="420"
        height="420"
        viewBox="0 0 420 420"
        aria-hidden="true"
      >
        <defs>
          <path
            id="circlePath"
            d="M 210, 210 m -170, 0 a 170,170 0 1,1 340,0 a 170,170 0 1,1 -340,0"
          />
        </defs>
        <text fontSize="34" fontWeight="900" letterSpacing="14">
          <textPath href="#circlePath" startOffset="0">
            BURGERS · TACOS · WINGS · BURGERS · TACOS · WINGS ·
          </textPath>
        </text>
        <circle cx="210" cy="210" r="170" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />
        <circle cx="210" cy="210" r="120" stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
      </svg>

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="inline-block bg-white/95 rounded-2xl p-3">
              <Logo size="lg" />
            </div>
            <p className="mt-5 text-white/65 text-sm max-w-xs leading-relaxed">
              Bold flavor. Made fresh. Built for you. Thank you for supporting
              local.
            </p>

            {/* Newsletter */}
            <form onSubmit={subscribe} className="mt-6 flex max-w-sm">
              <input
                type="email"
                placeholder="Email for deals & drops"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="newsletter-email-input"
                className="flex-1 rounded-l-full bg-white/5 border border-white/10 text-white placeholder:text-white/40 px-5 py-3 text-sm focus:outline-none focus:border-[#A3D900]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="newsletter-submit-btn"
                className="rounded-r-full bg-[#A3D900] hover:bg-[#8ebf00] text-[#0B0B0B] font-extrabold tracking-[0.18em] uppercase px-5 py-3 text-xs transition-colors flex items-center"
              >
                {loading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  "Join"
                )}
              </button>
            </form>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-[0.3em] text-white/90">
              Quick Links
            </h4>
            <ul className="mt-4 space-y-2.5 text-sm text-white/65">
              <li>
                <a href="/menu" className="hover:text-[#A3D900] transition-colors" data-testid="footer-link-menu">
                  Menu
                </a>
              </li>
              <li>
                <a
                  href={ORDER_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-order"
                  className="hover:text-[#A3D900] transition-colors"
                >
                  Order Now
                </a>
              </li>
              <li>
                <a href="#catering" className="hover:text-[#A3D900] transition-colors" data-testid="footer-link-catering">
                  Catering
                </a>
              </li>
              <li>
                <a href="#footer" className="hover:text-[#A3D900] transition-colors" data-testid="footer-link-locations">
                  Locations
                </a>
              </li>
              <li>
                <a href={`mailto:${emailAddr}`} className="hover:text-[#A3D900] transition-colors" data-testid="footer-link-contact">
                  Contact Us
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-[0.3em] text-white/90">
              Contact
            </h4>
            <ul className="mt-4 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 text-[#A3D900]" />
                <span>
                  {addr1}
                  <br />
                  {addr2}
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={16} className="text-[#A3D900]" />
                <a href={`tel:${tel}`} className="hover:text-white" data-testid="footer-phone">
                  {phone}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#A3D900]" />
                <a href={`mailto:${emailAddr}`} className="hover:text-white" data-testid="footer-email">
                  {emailAddr}
                </a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="font-display text-sm uppercase tracking-[0.3em] text-white/90">
              Hours
            </h4>
            <ul className="mt-4 space-y-2 text-sm text-white/70">
              {hours.map((h, i) => (
                <li key={`${h.days}-${i}`} className="flex justify-between gap-3">
                  <span>{h.days}</span>
                  <span className="text-white/90">{h.time}</span>
                </li>
              ))}
            </ul>

            <h4 className="mt-6 font-display text-sm uppercase tracking-[0.3em] text-white/90">
              Follow Us
            </h4>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <a
                href={ig}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Instagram ${igHandle}`}
                data-testid="social-instagram"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 hover:bg-[#A3D900] hover:text-[#0B0B0B] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href={fb}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Facebook ${igHandle}`}
                data-testid="social-facebook"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 hover:bg-[#A3D900] hover:text-[#0B0B0B] transition-colors"
              >
                <Facebook size={16} />
              </a>
              <a
                href={tt}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`TikTok ${ttHandle}`}
                data-testid="social-tiktok"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/5 hover:bg-[#A3D900] hover:text-[#0B0B0B] transition-colors"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.42a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.85z" />
                </svg>
              </a>
            </div>
            <div className="mt-3 space-y-1 text-xs text-white/55">
              <div>IG / FB · {igHandle}</div>
              <div>TikTok · {ttHandle}</div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 sm:justify-between text-xs text-white/45">
          <span>© {new Date().getFullYear()} BTW Burgers Tacos Wings. All rights reserved.</span>
          <div className="flex gap-5">
            <a href="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</a>
            <a href="/terms" className="hover:text-white" data-testid="footer-terms">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
